import React from 'react';
import { FileTextIcon, GlobeIcon, StarIcon } from '@components/common/ui/icons';
import { highlightMatch } from '@components/common/layout/topbar/utils/searchUtils';
import type { SearchResult } from '@components/common/layout/topbar/hooks/useSearch';
import * as styles from '@components/common/layout/topbar/TopBar.module.css';

const cn = (...classes: (string | undefined | false)[]) => classes.filter(Boolean).join(' ');

interface SearchResultItemProps {
    note: SearchResult;
    query: string;
    isFocused: boolean;
    onSelect: (noteId: string) => void;
    variant: 'own' | 'public';
}

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'short' });
}

export const SearchResultItem: React.FC<SearchResultItemProps> = ({
    note,
    query,
    isFocused,
    onSelect,
    variant,
}) => {
    const Icon = variant === 'public' ? GlobeIcon : FileTextIcon;

    // Right-side meta: folder path for own notes, date for public notes
    const rightMeta =
        variant === 'own'
            ? note.folderPath || null
            : note.updatedAt
              ? formatDate(note.updatedAt)
              : null;

    const excerpt = note.meta?.excerpt?.trim();

    return (
        <button
            type="button"
            className={cn(styles.cmdkItem, isFocused && styles.cmdkItemFocused)}
            onClick={() => onSelect(note.id)}
        >
            <Icon className={cn(styles.cmdkItemIcon, excerpt && styles.cmdkItemIconTop)} />
            <div className={styles.cmdkItemContent}>
                <span className={styles.cmdkItemTitle}>
                    {query ? highlightMatch(note.title || 'Untitled', query) : note.title || 'Untitled'}
                    {note.tags && note.tags.length > 0 && (
                        <span className={styles.cmdkItemTags}>
                            {note.tags.slice(0, 3).map((t) => (
                                <span key={t.name} className={styles.cmdkItemTag}>#{t.name}</span>
                            ))}
                        </span>
                    )}
                </span>
                {excerpt && (
                    <span className={styles.cmdkItemExcerpt}>{excerpt}</span>
                )}
            </div>
            {rightMeta && <span className={styles.cmdkItemMeta}>{rightMeta}</span>}
            {note.meta?.isFavorite && <StarIcon className={styles.searchResultStar} />}
        </button>
    );
};
